/**
 * Domain vocabulary shared by every client.
 *
 * These are presentation decisions that must not diverge between web and
 * mobile — what a tier is called, what an expiry state looks like, what the
 * capture point actually promises a buyer. Putting them here means a copy
 * change lands in both apps at once instead of in one of them.
 *
 * The RULES still live on the server (§9.12: every cap is enforced
 * server-side). Nothing in this file decides anything; it only names things.
 */

import type {
  BuyerTier,
  CaptureTrigger,
  DeliveryMethod,
  ExpiryState,
  RefundReasonKey,
  SubOrderStatus,
} from './types';

/**
 * §11 — colour is semantic, never decorative.
 *
 *   blue     the only decorative colour, and the brand
 *   green    verified, and nothing else
 *   amber    expiry or stock running out, and nothing else
 *   red      blocked or error, and nothing else
 *
 * Learn it once, read a shelf of forty products at a glance.
 */
export type SemanticTone = 'neutral' | 'brand' | 'verified' | 'caution' | 'danger';

export function expiryTone(state: ExpiryState | null | undefined): SemanticTone {
  switch (state) {
    case 'ok':
      return 'verified';
    case 'caution':
      return 'caution';
    case 'below_minimum':
    case 'expired':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function expiryLabel(state: ExpiryState | null | undefined, monthsRemaining?: number | null): string {
  switch (state) {
    case 'ok':
      return monthsRemaining ? `${monthsRemaining} months left` : 'In date';
    case 'caution':
      return monthsRemaining ? `Expires in ${monthsRemaining} months` : 'Expiring soon';
    case 'below_minimum':
      return 'Below our minimum shelf life';
    case 'expired':
      return 'Expired';
    default:
      return 'Expiry unknown';
  }
}

/**
 * How full the ExpiryMeter bar is, 0–100.
 *
 * Measured against a two-year nominal shelf life so that the bar means the same
 * thing on every product, rather than each product silently rescaling its own
 * axis and making a 6-month pack look identical to a 24-month one.
 */
export function expiryMeterPercent(monthsRemaining: number | null | undefined): number {
  if (!monthsRemaining || monthsRemaining <= 0) return 0;

  return Math.max(4, Math.min(100, Math.round((monthsRemaining / 24) * 100)));
}

// ---------------------------------------------------------------------------
// Tiers (§2)
// ---------------------------------------------------------------------------

export const buyerTierLabels: Record<BuyerTier, string> = {
  new: 'New buyer',
  standard: 'Standard',
  trusted: 'Trusted',
  restricted: 'Restricted',
};

// ---------------------------------------------------------------------------
// Capture (§1.3, §11)
// ---------------------------------------------------------------------------

/**
 * What the buyer is actually promised. §11 requires the door variant verbatim
 * at checkout; the server sends the authoritative string in `system/config`,
 * and this is the client-side fallback for a cart that has not loaded it yet.
 */
export function captureExplanation(
  trigger: CaptureTrigger,
  deliveryDisplay: string,
  goodsDisplay: string,
): string {
  switch (trigger) {
    case 'checkout':
      return `Delivery ${deliveryDisplay} and medicines ${goodsDisplay} are charged now.`;
    case 'dispatch':
      return `Delivery ${deliveryDisplay} — charged now, covers the rider's trip. Medicines ${goodsDisplay} — charged when your order leaves the pharmacy.`;
    case 'door':
      return `Delivery ${deliveryDisplay} — charged now, covers the rider's trip. Medicines ${goodsDisplay} — charged at your door, after you check the pack.`;
  }
}

export const captureShortLabel: Record<CaptureTrigger, string> = {
  checkout: 'Charged at checkout',
  dispatch: 'Charged when it leaves the pharmacy',
  door: 'Charged at your door',
};

// ---------------------------------------------------------------------------
// Order status (§7.4)
// ---------------------------------------------------------------------------

export const subOrderStatusLabels: Record<SubOrderStatus, string> = {
  pending_payment: 'Awaiting payment',
  pending_review: 'In review',
  paid: 'Paid',
  accepted: 'Accepted',
  packed: 'Packed',
  dispatched: 'Out for delivery',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  partially_refunded: 'Partly refunded',
  disputed: 'In dispute',
};

export function subOrderStatusTone(status: SubOrderStatus): SemanticTone {
  switch (status) {
    case 'completed':
    case 'delivered':
      return 'verified';
    case 'pending_review':
    case 'pending_payment':
      return 'caution';
    case 'cancelled':
    case 'disputed':
      return 'danger';
    case 'dispatched':
      return 'brand';
    default:
      return 'neutral';
  }
}

export const deliveryMethodLabels: Record<DeliveryMethod, string> = {
  standard: 'Standard',
  express: 'Express',
  pickup: 'Pick up at pharmacy',
};

// ---------------------------------------------------------------------------
// Refunds (§4)
// ---------------------------------------------------------------------------

export const refundReasonLabels: Record<RefundReasonKey, string> = {
  not_genuine: "I don't think this is genuine",
  adverse_reaction: 'I had a reaction to this',
  wrong_item: 'I received the wrong item',
  damaged: 'It arrived damaged',
  expired: 'It is expired or short-dated',
  wrong_quantity: 'The quantity is wrong',
  not_delivered: 'It never arrived',
};

/**
 * §9.17 — never gated, at any time, under any evidence. Exported as data so no
 * client can accidentally filter them out of a menu.
 */
export const alwaysAvailableRefundReasons: readonly RefundReasonKey[] = [
  'not_genuine',
  'adverse_reaction',
];

export function isSafetyRefundReason(reason: RefundReasonKey): boolean {
  return alwaysAvailableRefundReasons.includes(reason);
}
